import { useRef, useState } from "react";
import { Play, MapPin, Clock, Youtube, ExternalLink } from "lucide-react";
import { ETV_WIN_SERIES_URL } from "../lib/logos";

/**
 * 16:9 landscape episode reel — shows the FULL YouTube thumbnail uncropped.
 * - Static YouTube maxres thumbnail (16:9) is the entire visible area
 * - On hover, the YouTube iframe mounts and autoplays (muted, looped)
 * - On click, parent opens the ReelModal
 */
export const EpisodeReel = ({ episode, onOpen, index = 0 }) => {
  const [hover, setHover] = useState(false);
  const hoverTimer = useRef(null);

  const enter = () => { hoverTimer.current = setTimeout(() => setHover(true), 220); };
  const leave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHover(false);
  };

  return (
    <div
      data-testid={`episode-reel-${episode.id}`}
      onMouseEnter={enter}
      onMouseLeave={leave}
      className="group relative block w-full text-left st-interactive"
      style={{
        background: "var(--night)",
        border: "1px solid rgba(201,160,74,.14)",
        animation: `reelIn .8s ${index * 0.08}s both`,
      }}
    >
      {/* Clickable poster area opens trailer modal */}
      <button
        type="button"
        onClick={() => onOpen?.(episode)}
        className="block w-full text-left p-0 border-0"
        style={{ background: "transparent" }}
        aria-label={`Play trailer · ${episode.title}`}
      >
      {/* Full 16:9 thumbnail — uncropped */}
      <div className="relative w-full" style={{ aspectRatio: "16 / 9", overflow: "hidden" }}>
        <img
          src={episode.poster}
          alt={episode.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit: "cover", // YouTube maxres is exactly 16:9, so cover == full image
            filter: "brightness(.72) saturate(.95)",
            transform: hover ? "scale(1.04)" : "scale(1)",
            transition: "transform .8s ease, opacity .4s ease",
            opacity: hover ? 0 : 1,
          }}
        />

        {/* YouTube hover preview (16:9 fills 16:9 frame perfectly) */}
        {hover && (
          <iframe
            title={episode.title}
            src={episode.video}
            allow="autoplay; encrypted-media; picture-in-picture"
            className="absolute inset-0 w-full h-full"
            style={{ border: 0, pointerEvents: "none", filter: "brightness(.78) saturate(.95)" }}
          />
        )}

        {/* Subtle vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(5,4,3,.35) 0%, transparent 30%, transparent 65%, rgba(5,4,3,.92) 100%)",
          }}
        />

        {/* Top-left ep # */}
        <div className="absolute top-3 left-4 st-cinzel text-[0.5rem] tracking-[0.4em] text-[var(--gold-lt)] uppercase z-[2]">
          EP · {String(episode.number).padStart(2, "0")}
        </div>

        {/* Top-right duration */}
        <div className="absolute top-3 right-4 flex items-center gap-1.5 st-cinzel text-[0.5rem] tracking-[0.3em] text-[var(--gold-lt)] uppercase z-[2]">
          <Clock size={10}/> {episode.duration}
        </div>

        {/* Play badge */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center z-[2]"
          style={{
            width: 64, height: 64, borderRadius: "50%",
            border: "1.5px solid rgba(232,197,106,.7)",
            background: "rgba(8,6,4,.45)",
            backdropFilter: "blur(6px)",
            opacity: hover ? 0 : 1,
            transition: "opacity .3s, transform .3s",
            transform: hover ? "scale(.7)" : "scale(1)",
          }}
        >
          <Play size={22} className="text-[var(--gold-lt)] ml-1" fill="currentColor"/>
        </div>

        {/* YouTube tag bottom-left */}
        <div className="absolute bottom-3 left-4 flex items-center gap-1.5 st-cinzel text-[0.46rem] tracking-[0.3em] text-[#ff9595] uppercase z-[2]">
          <Youtube size={11}/> Official Trailer
        </div>
      </div>
      </button>

      {/* Info row below the image */}
      <div className="p-5">
        <div className="st-cormorant italic text-[0.95rem] text-[var(--gold-lt)] mb-1">{episode.guest}</div>
        <h3 className="st-cormorant text-xl md:text-2xl font-medium text-[var(--cream)] leading-tight">
          {episode.title}
        </h3>
        <div className="mt-2 flex items-center gap-1.5 st-cinzel text-[0.48rem] tracking-[0.32em] text-[var(--fog)] uppercase">
          <MapPin size={10}/> {episode.location}
        </div>

        {/* CTA row — Full Episode (ETV Win) + Trailer (opens modal) */}
        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href={ETV_WIN_SERIES_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`episode-watch-etvwin-${episode.id}`}
            className="inline-flex items-center gap-1.5 st-cinzel text-[0.46rem] tracking-[0.3em] uppercase px-3 py-2 st-interactive"
            style={{
              background: "#e21e26",
              color: "#ffffff",
              border: "1px solid #e21e26",
              textDecoration: "none",
            }}
          >
            <Play size={9} fill="currentColor"/> Full Episode <ExternalLink size={9}/>
          </a>
          <button
            type="button"
            onClick={() => onOpen?.(episode)}
            data-testid={`episode-watch-trailer-${episode.id}`}
            className="inline-flex items-center gap-1.5 st-cinzel text-[0.46rem] tracking-[0.3em] uppercase px-3 py-2 st-interactive"
            style={{
              border: "1px solid rgba(255,150,150,.5)",
              color: "#ff9595",
              background: "transparent",
            }}
          >
            <Youtube size={10}/> Trailer
          </button>
        </div>
      </div>

      <style>{`
        @keyframes reelIn {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
};
